import LoopVideo from "../../components/content/LoopVideo";
import CtaPanel from "../../components/sections/CtaPanel";
import LogoMarquee from "../../components/sections/LogoMarquee";
import Accent from "../../components/ui/Accent";
import { ButtonLink } from "../../components/ui/Button";
import SectionHeading from "../../components/ui/SectionHeading";
import { hero, math, process } from "../../data/home";
import { images } from "../../data/images";
import { base, localHref } from "../../lib/links";
import FitSelector from "./FitSelector";

function Hero() {
  return (
    <section className="blueprint blueprint-pan relative overflow-hidden px-4 text-white sm:px-6">
      <LoopVideo
        src={base + hero.video}
        poster={images.heroJobsite}
        className="fill"
      />
      <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-ink/80 to-ink/95" />
      <div className="relative z-10 mx-auto max-w-280 pt-20 md:pt-24">
        <div>
          <span className="eyebrow text-blue-soft">{hero.eyebrow}</span>
          <h1 className="brush font-display mt-5 mb-8 max-w-[820px] text-[44px] uppercase sm:text-[56px] md:text-[72px] lg:text-[84px]">
            <Accent>{hero.title}</Accent>
          </h1>
          <p className="mb-9 max-w-[620px] text-lg text-white/85 md:text-[21px]">
            {hero.text}
          </p>
          <div className="flex flex-wrap gap-3.5 pb-20 md:pb-24">
            <ButtonLink />
            <ButtonLink href="#fit" variant="glass">
              {hero.secondary} ↓
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="bg-white px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading
          eyebrow={process.eyebrow}
          title={process.title}
          className="mb-11"
        >
          <p>{process.text}</p>
        </SectionHeading>
        <ol className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {process.steps.map(({ title, text }, i) => (
            <li
              key={title}
              className="rounded-b-brand border-t-4 border-yellow bg-surface px-6 pt-6 pb-7"
            >
              <span className="tag text-blue-deep">Step {i + 1}</span>
              <h3 className="font-display mt-2 mb-2.5 text-2xl text-slate uppercase">
                {title}
              </h3>
              <p className="text-[15.5px] text-muted">{text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function TheMath() {
  return (
    <section className="blueprint px-4 py-20 text-white sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading dark eyebrow={math.eyebrow} title={math.title}>
          <p className="max-w-[640px] text-lg">{math.text}</p>
        </SectionHeading>
        <div className="mt-11 grid grid-cols-1 items-center gap-7 lg:grid-cols-[1fr_auto_1fr]">
          <div>
            <span className="tag mb-3.5 block text-[13px] font-semibold text-white/65">
              {math.pileLabel}
            </span>
            <ul
              aria-label="A pile of overlapping software subscriptions"
              className="flex flex-wrap gap-2.5"
            >
              {math.pile.map((item) => (
                <li
                  key={item}
                  className="rounded-md border border-white/20 bg-white/8 px-3.5 py-2 text-sm font-bold text-white/60 line-through decoration-yellow/80 decoration-2"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div
            aria-hidden="true"
            className="font-display justify-self-center text-[42px] leading-none text-yellow max-lg:rotate-90"
          >
            →
          </div>
          <div>
            <span className="tag mb-3.5 block text-[13px] font-semibold text-yellow">
              {math.oneLabel}
            </span>
            <div className="rounded-brand bg-yellow px-7 pt-7 pb-6 text-ink">
              <h3 className="font-display text-[26px] leading-[1.15] uppercase">
                {math.oneTitle}
              </h3>
              <p className="mt-2.5 text-[15.5px] font-bold">{math.oneText}</p>
            </div>
          </div>
        </div>
        <div className="mt-11 flex flex-wrap items-center justify-between gap-x-10 gap-y-5 border-t border-white/15 pt-7">
          <p className="max-w-[660px] text-lg text-white/90">
            {math.bottom}{" "}
            <strong className="text-yellow">{math.bottomStrong}</strong>
          </p>
          <a
            href={localHref(math.href)}
            className="border-b-2 border-yellow pb-0.5 font-bold whitespace-nowrap text-white transition-colors hover:text-yellow"
          >
            {math.link} →
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <Hero />
      <LogoMarquee />
      <FitSelector />
      <Process />
      <TheMath />
      <CtaPanel />
    </>
  );
}
